import React from 'react';
import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import ActionButtonComponent, { ActionShape } from './action-button';
import UUID from 'uuid';
import { ComponentUtil } from 'ag-grid-community';

import { SYMBOL_DIRTY, SYMBOL_EXISTING, SYMBOL_INITIAL } from './symbols';

class CustomAGTable extends React.Component {

  state = {

  };

  constructor(props) {
    super(props);
    this.state.gridOptions = {
      frameworkComponents: {
        ActionButtonComponent
      },
      onGridReady: this.onGridReady,
      onCellEditingStarted: this.onCellEditingStarted,
      onCellEditingStopped: this.onCellEditingStopped,
      getRowNodeId: this.getRowNodeId,
      columnDefs: [],
      rowData: []
    }
  }

  getRowNodeId = data => {
    if (data && data[this.props.identifier]) {
      return data[this.props.identifier];
    }
    console.warn(`WARNING! Data has been provided to CustomAGTable without ${this.props.identifier}`, data);
    return null;
  };

  /**
   * @param {GridReadyEvent} event
   */
  onGridReady = event => {
    this.gridApi = event.api;
    if (this.props.sizeColumnsToFit) {
      this.gridApi.sizeColumnsToFit();
    }
    this.reactAGBridge();
  };

  componentDidUpdate() {
    if (!this.gridApi) {
      return;
    }
    this.reactAGBridge();
  }

  reactAGBridge() {
    const { identifier } = this.props;

    const cleanIdentifiers = [],
      dirtyIdentifiers = [],
      cleanNodes = {};

    this.gridApi.forEachNode(
      node => {
        const { data: { [identifier]: currentIdentifier, [SYMBOL_DIRTY]: dirty }} = node;
        if (!currentIdentifier) {
          return;
        }
        if (dirty) {
          dirtyIdentifiers.push(currentIdentifier);
        } else {
          cleanIdentifiers.push(currentIdentifier);
          cleanNodes[currentIdentifier] = node;
        }
      }
    );

    const add = this.props.items
      .filter(({ [identifier]: currentIdentifier }) => (
        currentIdentifier &&
        cleanIdentifiers.indexOf(currentIdentifier) === -1 &&
        dirtyIdentifiers.indexOf(currentIdentifier) === -1
      ))
      .map(data => ({
        [SYMBOL_EXISTING]: true,
        ...data,
        actions: []
          .concat(this.props.defaultActions)
          .concat(this.props.cleanActions)
          .filter(value => value)
      }));

    const update = this.props.items
      .filter(({ [identifier]: currentIdentifier }) => cleanIdentifiers.indexOf(currentIdentifier) !== -1)
      .map(data => ({
        [SYMBOL_EXISTING]: true,
        ...data,
        actions: []
          .concat(this.props.defaultActions)
          .concat(this.props.cleanActions)
          .filter(value => value)
      }));

    const remove = cleanIdentifiers
      .filter(currentIdentifier => {
        const foundIndex = this.props.items
          .findIndex(({ [identifier]: otherIdentifier }) => otherIdentifier === currentIdentifier);
        return foundIndex === -1;
      })
      .map(currentIdentifier => cleanNodes[currentIdentifier]);

    this.gridApi.updateRowData({
      add,
      update,
      remove
    });
  }

  /**
   * @param {CellEditingStartedEvent} event
   */
  onCellEditingStarted = event => {
    event.node.setData({
      ...event.data,
      actions: []
        .concat(this.props.defaultActions)
        .concat(this.props.dirtyActions)
        .filter(value => value),
      [SYMBOL_DIRTY]: true,
      [SYMBOL_INITIAL]: {
        ...event.data,
        actions: []
          .concat(this.props.defaultActions)
          .concat(this.props.cleanActions)
          .filter(value => value)
      }
    });
  };

  setNodeData = (node, data = {}) => {
    node.setData({
      ...data,
      [SYMBOL_INITIAL]: null,
      [SYMBOL_DIRTY]: false,
      actions: []
        .concat(this.props.defaultActions)
        .concat(this.props.cleanActions)
        .filter(value => value)
    })
  };

  add = (data = {}) => {
    if (!this.gridApi) {
      throw new Error('No gridApi loaded yet');
    }
    const newData = {
      [this.props.identifier]: this.props.createIdentifier(),
      ...(data || {}),
      [SYMBOL_DIRTY]: true,
      actions: []
        .concat(this.props.defaultActions)
        .concat(this.props.dirtyActions)
        .filter(value => value)
    };
    this.gridApi.updateRowData({
      add: [
        newData
      ]
    })
  };

  /**
   * @param {CellEditingStoppedEvent} event
   */
  onCellEditingStopped = event => {

  };

  getEventProps() {
    return ComponentUtil.getEventCallbacks()
      .filter(key => this.state.gridOptions[key] instanceof Function && this.props[key] instanceof Function)
      .reduce((map, key) => {
        const fn = (...args) => {
          if (this.state.gridOptions[key] instanceof Function) {
            this.state.gridOptions[key].apply(null, args);
          }
          if (this.props[key] instanceof Function) {
            this.props[key].call(null, ...args, this);
          }
        };
        return {
          ...map,
          [key]: fn
        }
      }, {});
  }

  getColumnDefinitions() {
    const actionColumn = {
      field: 'actions',
      cellRenderer: 'ActionButtonComponent',
      colId: 'params',
      singleClickEdit: true,
      pinned: 'right',
      sortable: false,
      lockPosition: true,
      suppressMenu: true,
      suppressMovable: false
    };
    const columnDefs = Array.isArray(this.props.columnDefs) ? this.props.columnDefs : [];
    const baseColumnDefs = []
      .concat(this.state.gridOptions.columnDefs)
      .concat(columnDefs)
      .filter(value => value)
      .filter(({ field }) => field !== actionColumn.field);

    const actions = []
      .concat(this.props.defaultActions)
      .concat(this.props.cleanActions)
      .concat(this.props.dirtyActions)
      .filter(value => value);

    if (!actions.length) {
      return baseColumnDefs;
    }

    return baseColumnDefs.concat(actionColumn);
  }

  render() {
    return (
      <AgGridReact
        gridOptions={{...this.state.gridOptions}}
        {...this.props}
        frameworkComponents={{
          ...(this.state.gridOptions.frameworkComponents || {}),
          ...(this.props.frameworkComponents || {})
        }}
        {...this.getEventProps()}
        columnDefs={this.getColumnDefinitions()}
      />
    );
  }

}

CustomAGTable.defaultProps = {
  enableColResize: true,
  sizeColumnsToFit: true,
  defaultActions: [],
  cleanActions: [],
  dirtyActions: [],
  items: [],
  identifier: 'uuid',
  createIdentifier: () => UUID.v4()
};

CustomAGTable.propTypes = {
  name: PropTypes.string.isRequired,
  enableColResize: PropTypes.bool,
  key: PropTypes.string,
  columnDefs: PropTypes.array,
  // TODO defaultActions don't update dirty data
  defaultActions: PropTypes.arrayOf(
    ActionShape.isRequired
  ),
  cleanActions: PropTypes.arrayOf(
    ActionShape.isRequired
  ),
  dirtyActions: PropTypes.arrayOf(
    ActionShape.isRequired
  ),
  items: PropTypes.arrayOf(
    PropTypes.object.isRequired
  ),
  sizeColumnsToFit: PropTypes.bool,
  identifier: PropTypes.string,
  createIdentifier: PropTypes.func
};

export default CustomAGTable;



// WEBPACK FOOTER //
// ./src/views/common/ag-table.js